/**
 * CharacterWizard Page
 *
 * Multi-step wizard for creating or editing a character.
 * Steps: Name & Avatar -> Traits -> Wants & Fears -> Secret & Item -> Voice
 */

import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import {
  NameAvatarStep,
  TraitsStep,
  WantsFearStep,
  SecretItemStep,
  VoiceStep,
  ProgressIndicator,
  TemplateCard,
} from '@/components/characters'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getCharacter,
  createCharacter,
  updateCharacter,
  STARTER_TEMPLATES,
  type CharacterWizardData,
} from '@/services/characterLibraryService'

const WIZARD_STEPS = [
  { id: 'name', label: 'Name', component: NameAvatarStep },
  { id: 'traits', label: 'Traits', component: TraitsStep },
  { id: 'wants', label: 'Motivation', component: WantsFearStep },
  { id: 'secret', label: 'Depth', component: SecretItemStep },
  { id: 'voice', label: 'Voice', component: VoiceStep },
]

const STEP_LABELS = WIZARD_STEPS.map(s => s.label)

const DEFAULT_DATA: CharacterWizardData = {
  name: '',
  avatar_emoji: '👤',
  traits: [],
  wants: '',
  fear: '',
  secret: '',
  signature_item: '',
  voice_sample: '',
  backstory: '',
}

export function CharacterWizard() {
  const { characterId } = useParams<{ characterId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const isEditing = !!characterId && characterId !== 'new'

  const [currentStep, setCurrentStep] = useState(0)
  const [data, setData] = useState<CharacterWizardData>(DEFAULT_DATA)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSaving, setIsSaving] = useState(false)
  const [showTemplates, setShowTemplates] = useState(!isEditing)

  // Load existing character if editing
  useEffect(() => {
    async function loadCharacter() {
      if (!isEditing || !characterId) return

      setIsLoading(true)
      try {
        const character = await getCharacter(characterId)
        if (!character) {
          showToast('Character not found', 'error')
          navigate('/characters')
          return
        }

        setData({
          name: character.name,
          avatar_emoji: character.avatar_emoji,
          traits: (character.traits as string[]) || [],
          wants: character.wants || '',
          fear: character.fear || '',
          secret: character.secret || '',
          signature_item: character.signature_item || '',
          voice_sample: character.voice_sample || '',
          backstory: character.backstory || '',
        })
        setShowTemplates(false)
      } catch (error) {
        console.error('Error loading character:', error)
        showToast('Failed to load character', 'error')
        navigate('/characters')
      } finally {
        setIsLoading(false)
      }
    }

    loadCharacter()
  }, [characterId, isEditing, navigate, showToast])

  // Handle data changes
  const handleChange = (updates: Partial<CharacterWizardData>) => {
    setData(prev => ({ ...prev, ...updates }))
  }

  // Handle template selection
  const handleSelectTemplate = (template: typeof STARTER_TEMPLATES[0]) => {
    setData({
      ...DEFAULT_DATA,
      name: template.name,
      avatar_emoji: template.avatar_emoji,
      traits: template.traits,
      wants: template.wants,
      fear: template.fear,
    })
    setShowTemplates(false)
  }

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 0:
        return data.name.trim().length >= 2
      default:
        return true
    }
  }

  const goNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  // Save character
  const handleSave = async () => {
    if (!activeWriter) return

    setIsSaving(true)
    try {
      if (isEditing && characterId) {
        await updateCharacter(characterId, data)
        showToast('Character updated!', 'success')
      } else {
        await createCharacter(activeWriter.id, data)
        showToast('Character created!', 'success')
      }
      navigate('/characters')
    } catch (error) {
      console.error('Error saving character:', error)
      showToast('Failed to save character', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const CurrentStepComponent = WIZARD_STEPS[currentStep].component
  const isLastStep = currentStep === WIZARD_STEPS.length - 1

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-secondary)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/characters">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="font-display font-bold text-xl text-[var(--color-kids-text)]">
              {isEditing ? 'Edit Character' : 'Create Character'}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Template Selection (only for new characters) */}
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[var(--color-kids-primary)]" />
                  <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)]">
                    Start with a Template
                  </h2>
                </div>
                <p className="text-sm text-[var(--color-kids-text-muted)] mb-4">
                  Choose a template to get started, or create from scratch.
                </p>
                <div className="space-y-3">
                  {STARTER_TEMPLATES.map((template) => (
                    <TemplateCard
                      key={template.name}
                      template={template}
                      onClick={() => handleSelectTemplate(template)}
                    />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  className="w-full mt-4"
                  onClick={() => setShowTemplates(false)}
                >
                  Start from Scratch
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Wizard */}
        {!showTemplates && (
          <>
            {/* Progress */}
            <ProgressIndicator
              currentStep={currentStep}
              totalSteps={WIZARD_STEPS.length}
              labels={STEP_LABELS}
            />

            {/* Step Content */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CurrentStepComponent data={data} onChange={handleChange} />
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </Button>

              <div className="flex items-center gap-2">
                {!isEditing && currentStep === 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowTemplates(true)}
                  >
                    Templates
                  </Button>
                )}

                {isLastStep ? (
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!canGoNext() || isSaving}
                    isLoading={isSaving}
                  >
                    {isEditing ? 'Save Changes' : 'Create Character'}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={goNext}
                    disabled={!canGoNext()}
                  >
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                )}
              </div>
            </div>

            {/* Skip hint */}
            {currentStep > 0 && !isLastStep && (
              <p className="text-center text-xs text-[var(--color-kids-text-muted)] mt-4">
                Fields are optional. Skip ahead if you want!
              </p>
            )}
          </>
        )}
      </main>
    </div>
  )
}

export default CharacterWizard
